import React, { useState, useEffect } from "react";
import { extractSettlmentData } from "../../store/modules/document/documentActions";
import { insertSettlementData } from "../../store/modules/transactions/transactionActions";

import PreviewSettlementDialog from "../../ui-components/import-export/PreviewSettlementDialog";
import { Snackbar, Alert, useTheme } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  deleteTransaction,
  getTransactionsByYear,
  updatePipelineStatus,
} from "../../store/modules/transactions/transactionActions";
import {
  Breadcrumbs,
  CardActionArea,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { Link } from "react-router-dom";
import {
  useMediaQuery,
  Theme,
  Stack,
  Card,
  IconButton,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Collapse,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Paper,
} from "@mui/material";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClearIcon from "@mui/icons-material/Clear";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useDndContext,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { PageLayout } from "../../ui-components/PageLayout";
import AddIcon from "@mui/icons-material/Add";
import {
  IAddressModel,
  IExtractSettlementDataModel,
  ILookupItemSimpleModel,
} from "../../types/types";
import LoadingDialog from "../../ui-components/Dialogs/LoadingDialog";
import { RootState } from "../../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppDispatch } from "../../store/store";
import { getUserCustomData } from "../../store/modules/common/commonActions";
import ContractUploadDialog from "../../ui-components/import-export/ContractUploadDialog";
import { removeCounty } from "../../utils/common";
import PipelineSummaryContainer from "../../ui-components/Dashboard/PipelineSummaryContainer";
import {
  PIPELINE_ROUTE,
  closingDateLabel,
  statusColor,
  transactionStatusLabel,
  TransactionStatus,
  usePipelineFolder,
} from "../../hooks/usePipeline";

// Pipeline sub-tables, top to bottom: closest to closing first
const PIPELINE_SECTIONS = [
  TransactionStatus.UnderContract,
  TransactionStatus.Active,
  TransactionStatus.Lead,
];

const DRAG_HANDLE_TOOLTIP = "Drag to another section to change the Status";

/** The ⋮⋮ handle a Pipeline entry is dragged by (mouse or touch) */
const PipelineDragHandle: React.FC<{
  id: number;
  status: TransactionStatus;
  className?: string;
  color?: string;
}> = ({ id, status, className, color = "text.disabled" }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
    data: { status },
  });
  return (
    // No tooltip on touch: a press on the handle starts a drag
    <Tooltip title={DRAG_HANDLE_TOOLTIP} disableTouchListener>
      <Box
        component="span"
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        className={className}
        // Clicking the handle shouldn't open the entry
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        // touch-action: none keeps a press on the handle from scrolling the page
        sx={{ display: "inline-flex", cursor: "grab", touchAction: "none", color }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

/** A Pipeline section an entry can be dropped on to take that Status */
const PipelineDropSection: React.FC<{
  status: TransactionStatus;
  children: React.ReactNode;
}> = ({ status, children }) => {
  const theme = useTheme();
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const { active } = useDndContext();
  const canDrop = !!active && active.data.current?.status !== status;
  return (
    <Box
      ref={setNodeRef}
      sx={{
        mt: 3,
        borderRadius: 1,
        // While dragging, the other sections show where the entry can go
        outline: canDrop
          ? `2px dashed ${statusColor(theme, status)}`
          : "2px dashed transparent",
        outlineOffset: 4,
        bgcolor: canDrop && isOver ? "action.hover" : undefined,
      }}
    >
      {children}
    </Box>
  );
};

interface TransactionSearchFilters {
  clientName: string;
  typeId: number[];
  sourceId: number[];
  areaId: number[];
  reasonId: number[];
  address: string;
  priceMin: number | "";
  priceMax: number | "";
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

//Average price can be displayed in the year summary on top of the year’s dashboard.
// Total volume of a year can be displayed in the year’s summary on top of the year’s dashboard.
// Landlords and Tenants Tickets should have “Monthly Rent” instead of “Price.” It should not be used in calculating Annual Total Volume or Annual Average Price.
const TransactionListing: React.FC = () => {
  const { year } = useParams<{ year: string }>();
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const { currentUser } = useSelector((state: RootState) => state?.user);
  // "/transactions/pipeline" is the Pipeline folder (Lead/Active entries)
  const isPipeline = year === PIPELINE_ROUTE;
  const { data: pipelineFolder } = usePipelineFolder();
  const derivedYearId = isPipeline
    ? pipelineFolder?.id
    : years?.find((y) => y.year === Number(year))?.id;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [openSettlementPreview, setOpenSettlementPreview] = useState(false);
  const [settlementPreview, setSettlementPreview] =
    useState<IExtractSettlementDataModel>();
  const [isImportingContracts, setIsImportingContracts] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "info" | "error",
  });

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchFilters, setSearchFilters] = useState<TransactionSearchFilters>({
    clientName: "",
    typeId: [],
    sourceId: [],
    areaId: [],
    reasonId: [],
    address: "",
    priceMin: "",
    priceMax: "",
    dateFrom: null,
    dateTo: null,
  });
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);

  // const [error, setError] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: number | undefined;
    confirmed?: boolean | undefined;
  }>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [currentItemId, setCurrentItemId] = useState<number | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  // useEffect(() => {
  //   if (years && year && !derivedYearId) {
  //     setError(`No data found for year ${year}`);
  //   } else {
  //     setError(null);
  //   }
  // }, [years, year, derivedYearId]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const isTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("sm", "md"),
  );

  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const dispatch = useDispatch<AppDispatch>();

  const {
    loaded: userDataLoaded,
    loading: userDataLoading,
    data: userData,
  } = useSelector((state: RootState) => state.commonData.userData);
  useQuery({
    queryKey: ["userCustomDatalist"],
    queryFn: () => {
      // if (!userDataLoaded) {
      dispatch(getUserCustomData());
      // }
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,

    // enabled: !userDataLoaded,
  });

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["transactions", derivedYearId],
    queryFn: () =>
      getTransactionsByYear(
        Number(derivedYearId),
        1,
        50,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
      ),
    enabled: !!derivedYearId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!transactions) {
      setFilteredTransactions([]);
      return;
    }

    const filtered = transactions.filter((transaction) => {
      // Client name filter
      if (searchFilters.clientName) {
        const clientNames =
          transaction.clients
            ?.map((c: any) => c.name.toLowerCase())
            .join(" ") || "";
        if (!clientNames.includes(searchFilters.clientName.toLowerCase())) {
          return false;
        }
      }

      // Type filter
      if (
        searchFilters.typeId.length > 0 &&
        (transaction.typeId === undefined ||
          !searchFilters.typeId.includes(transaction.typeId))
      ) {
        return false;
      }

      // Source filter
      if (
        searchFilters.sourceId.length > 0 &&
        (transaction.sourceId === undefined ||
          !searchFilters.sourceId.includes(transaction.sourceId))
      ) {
        return false;
      }

      // Area filter
      if (
        searchFilters.areaId.length > 0 &&
        (transaction.areaId === undefined ||
          !searchFilters.areaId.includes(transaction.areaId))
      ) {
        return false;
      }

      // Reason filter
      if (
        searchFilters.reasonId.length > 0 &&
        (transaction.reasonId === undefined ||
          !searchFilters.reasonId.includes(transaction.reasonId))
      ) {
        return false;
      }

      // Address filter
      if (searchFilters.address) {
        const address =
          transaction.address?.formattedAddress?.toLowerCase() || "";
        if (!address.includes(searchFilters.address.toLowerCase())) {
          return false;
        }
      }

      // Price range filter
      if (
        searchFilters.priceMin &&
        transaction.price &&
        transaction.price < searchFilters.priceMin
      ) {
        return false;
      }
      if (
        searchFilters.priceMax &&
        transaction.price &&
        transaction.price > searchFilters.priceMax
      ) {
        return false;
      }

      // Date range filter
      if (
        searchFilters.dateFrom &&
        transaction.date &&
        transaction.date.isBefore(searchFilters.dateFrom)
      ) {
        return false;
      }
      if (
        searchFilters.dateTo &&
        transaction.date &&
        transaction.date.isAfter(searchFilters.dateTo)
      ) {
        return false;
      }

      return true;
    });

    setFilteredTransactions(filtered);
  }, [transactions, searchFilters]);

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      searchFilters.clientName ||
      searchFilters.typeId.length > 0 ||
      searchFilters.sourceId.length > 0 ||
      searchFilters.areaId.length > 0 ||
      searchFilters.reasonId.length > 0 ||
      searchFilters.address ||
      searchFilters.priceMin ||
      searchFilters.priceMax ||
      searchFilters.dateFrom ||
      searchFilters.dateTo
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchFilters({
      clientName: "",
      typeId: [],
      sourceId: [],
      areaId: [],
      reasonId: [],
      address: "",
      priceMin: "",
      priceMax: "",
      dateFrom: null,
      dateTo: null,
    });
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (searchFilters.clientName) count++;
    if (searchFilters.typeId.length > 0) count++;
    if (searchFilters.sourceId.length > 0) count++;
    if (searchFilters.areaId.length > 0) count++;
    if (searchFilters.reasonId.length > 0) count++;
    if (searchFilters.address) count++;
    if (searchFilters.priceMin) count++;
    if (searchFilters.priceMax) count++;
    if (searchFilters.dateFrom) count++;
    if (searchFilters.dateTo) count++;
    return count;
  };

  const displayTransactions = hasActiveFilters()
    ? filteredTransactions
    : transactions;
  const overdueCount = transactions.filter((t) => t.isOverdue).length;

  const handleDelete = (id: number) => {
    setDeleteConfirmation({ open: true, id: id });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setCurrentItemId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentItemId(null);
  };

  const handleDeleteFromMenu = () => {
    handleMenuClose();
    if (currentItemId !== null) {
      handleDelete(currentItemId);
    }
  };
  const { isLoading: isDeleting, data: deletedCount } = useQuery({
    queryKey: [
      "deleteTransaction",
      deleteConfirmation?.confirmed,
      deleteConfirmation?.id,
    ],
    queryFn: () => deleteTransaction(deleteConfirmation!.id!),
    enabled:
      deleteConfirmation?.confirmed === true &&
      deleteConfirmation?.id !== undefined,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isLoading === true) {
      setDeleteConfirmation({
        open: false,
        confirmed: false,
        id: deleteConfirmation?.id,
      });
    }
  }, [isLoading]);

  const queryClient = useQueryClient();

  // Pipeline drag and drop (by the handle, mouse or touch): dropping an entry on
  // another section changes its Status
  const [activeId, setActiveId] = useState<number | null>(null);
  const dragSensors = useSensors(
    // A plain click on the handle isn't a drag
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );
  const activeRow =
    activeId !== null ? transactions.find((t) => t.id === activeId) : undefined;

  const handleDragStart = ({ active }: DragStartEvent) =>
    setActiveId(Number(active.id));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (over) {
      handleStatusDrop(
        Number(active.id),
        active.data.current?.status,
        over.id as TransactionStatus,
      );
    }
  };

  const handleStatusDrop = async (
    id: number,
    fromStatus: TransactionStatus,
    toStatus: TransactionStatus,
  ) => {
    if (fromStatus === toStatus) return;

    const row = transactions.find((t) => t.id === id);
    if (
      toStatus === TransactionStatus.UnderContract &&
      !row?.address?.formattedAddress
    ) {
      setSnackbar({
        open: true,
        message: "Add an address before moving to Under Contract.",
        severity: "error",
      });
      return;
    }

    // Move the row right away; put it back if the server refuses
    const queryKey = ["transactions", derivedYearId];
    const previous = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (rows: any[] | undefined) =>
      rows?.map((r) => (r.id === id ? { ...r, status: toStatus } : r)),
    );

    try {
      const result = await updatePipelineStatus(id, toStatus);
      if (result?.duplicateId) {
        queryClient.setQueryData(queryKey, previous);
        setSnackbar({
          open: true,
          message:
            "This entry matches an existing transaction. Open it and change the Status there to resolve.",
          severity: "error",
        });
        return;
      }
      setSnackbar({
        open: true,
        message: `Moved to ${transactionStatusLabel(toStatus)}`,
        severity: "success",
      });
    } catch (error: any) {
      queryClient.setQueryData(queryKey, previous);
      setSnackbar({
        open: true,
        message:
          typeof error?.result === "string"
            ? error.result
            : "Couldn't move the entry. Please try again.",
        severity: "error",
      });
    } finally {
      // Section totals, the Pipeline summary and the overdue alert
      ["transactions", "pipelineFolder", "dashboardSummary"].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
    }
  };

  useEffect(() => {
    if (deletedCount !== undefined && deletedCount > 0) {
      // Refetch transactions after deletion
      queryClient.invalidateQueries({
        queryKey: ["transactions", derivedYearId],
      });

      setDeleteConfirmation({
        open: false,
        confirmed: false,
        id: undefined,
      });
    }
  }, [deletedCount]);

  function getTypeDecription(typeId: number | undefined): React.ReactNode {
    const result = userData?.transactionTypes?.find((x) => x.id === typeId);
    if (result) {
      return result.name;
    }
  }

  function getAreaDecription(sourceId: number | undefined): React.ReactNode {
    const source = userData?.areas?.find((x) => x.id === sourceId);
    if (source) {
      return source.name;
    }
  }

  function getReasonDecription(resaonId: number | undefined): React.ReactNode {
    const result = userData?.reasons?.find((x) => x.id === resaonId);
    if (result) {
      return result.name;
    }
  }

  /** The cards or the table for a set of rows: a year folder, or one status of the Pipeline */
  const renderTransactions = (rows: any[]) =>
    isMobile || isTablet || viewMode === "cards" ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          {rows.length === 0 && (
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {hasActiveFilters()
                  ? "No transactions match your search criteria."
                  : "No transactions found."}
              </Typography>
            </Grid>
          )}
          {rows.map((row) => (
            <Grid key={row.id} size={{ xs: 12, md: 4, sm: 6 }}>
              <Card
                elevation={3}
                sx={{
                  // The entry being dragged fades; the drag preview follows the pointer
                  opacity: row.id === activeId ? 0.4 : 1,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                  ...(row.isOverdue && {
                    border: "1px solid",
                    borderColor: "error.main",
                    bgcolor: "rgba(211, 47, 47, 0.06)",
                  }),
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/transactions/view/${row.id}`)}
                  sx={{
                    flexGrow: 1,
                    width: "100%",
                  }}
                >
                  <Box p={2} sx={{ flexGrow: 1 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {row.address?.latitude != null &&
                          row.address?.longitude != null && (
                            <Tooltip title="Verified Address">
                              <PlaceIcon color="primary" />
                            </Tooltip>
                          )}
                        <Typography variant="h6" color="primary">
                          {row.address?.formattedAddress ?? ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography variant="body2">
                        Type: {getTypeDecription(row.typeId)}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color={row.isOverdue ? "error" : "text.secondary"}
                        >
                          {row.date?.format("MMM/DD/YYYY")}
                        </Typography>
                      </Box>
                    </Box>
                    {row.address?.county && (
                      <Box mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          {row.address?.county
                            ? `${row.address.county}`
                            : ""}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        pb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        Clients:{" "}
                        {row.clients
                          ?.map((client: { name: any }) => client.name)
                          .join(", ")}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      $
                      {row.price?.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>

                    <Box mt={2} display="flex" justifyContent="center">
                      <Box
                        sx={{
                          height: 40,
                          width: "100%",
                          bgcolor: "rgba(0,0,0,0.04)",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          View Details
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardActionArea>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    p: 1,
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  {isPipeline && (
                    <Box sx={{ mr: "auto", display: "flex" }}>
                      <PipelineDragHandle
                        id={row.id}
                        status={row.status}
                        color="text.secondary"
                      />
                    </Box>
                  )}
                  <IconButton
                    size="small"
                    title="Edit"
                    color="primary"
                    onClick={() => navigate(`/transactions/edit/${row.id}`)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, row.id!);
                    }}
                    color="inherit"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    ) : (
      // </Grid>

      <Box
        sx={{
          pt: 1,
          width: "100%",
          overflow: "hidden",
          "& .MuiDataGrid-root": {
            width: "99%",
          },
        }}
      >
        <DataGrid
          autoHeight
          density="compact"
          disableColumnResize={true}
          disableColumnSelector
          // disableColumnMenu
          onRowClick={(params) =>
            navigate(`/transactions/view/${params.id}`)
          }
          getRowClassName={(params) =>
            [
              params.row.isOverdue ? "row-overdue" : "",
              params.row.id === activeId ? "row-dragging" : "",
            ].join(" ")
          }
          sx={{
            width: "100%",
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "hidden",
            },
            "& .row-overdue": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.14)",
              },
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            },
            "& .MuiDataGrid-row:hover .drag-handle": {
              color: "text.secondary",
            },
            // The row being dragged fades; the drag preview follows the pointer
            "& .row-dragging": {
              opacity: 0.4,
            },
          }}
          rows={rows}
          columns={[
            ...(isPipeline
              ? [
                  {
                    field: "dragHandle",
                    headerName: "",
                    width: 36,
                    minWidth: 36,
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                    renderCell: (params) => (
                      <Box
                        sx={{ display: "flex", alignItems: "center", height: "100%" }}
                      >
                        <PipelineDragHandle
                          id={params.row.id}
                          status={params.row.status}
                          className="drag-handle"
                        />
                      </Box>
                    ),
                  } as GridColDef,
                ]
              : []),
            {
              field: "typeId",
              headerName: "Type",
              // Pipeline has extra columns, so it runs narrower to avoid a horizontal scrollbar
              minWidth: isPipeline ? 80 : 100,
              flex: isPipeline ? 0.6 : 1,
              filterable: false,
              valueFormatter: (params: number) => getTypeDecription(params),
              sortable: true,
              sortingOrder: ["asc", "desc"],
              sortComparator: (v1, v2, cellParams1, cellParams2) => {
                const type1 = userData?.transactionTypes?.find(
                  (x) => x.id === v1,
                );
                const type2 = userData?.transactionTypes?.find(
                  (x) => x.id === v2,
                );

                return (type1?.name ?? "").localeCompare(type2?.name ?? "");
              },
            },
            ...(isPipeline
              ? [
                  {
                    field: "leadReceivedDate",
                    headerName: "Lead Received Date",
                    minWidth: 150,
                    flex: 1,
                    valueFormatter: (params: Dayjs) =>
                      params?.format("MMM/DD/YYYY") || "",
                  },
                ]
              : []),
            {
              field: "date",
              headerName: closingDateLabel(
                isPipeline ? TransactionStatus.Lead : TransactionStatus.Transaction,
              ),
              minWidth: isPipeline ? 160 : 200,
              flex: isPipeline ? 0.8 : 1,
              valueFormatter: (params: Dayjs) =>
                params?.format("MMM/DD/YYYY") || "",
              sortable: true,
              sortingOrder: ["asc", "desc"],
              renderCell: (params) => {
                const transaction = params.row;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={transaction.isOverdue ? "error" : undefined}
                    >
                      {params.value?.format("MMM/DD/YYYY") || ""}
                    </Typography>
                  </Box>
                );
              },
            },
            {
              field: "address",
              headerName: "Address",
              minWidth: 200,
              flex: 2,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              valueFormatter: (params: IAddressModel) => {
                return params?.formattedAddress || "";
              },
              renderCell: (params) => {
                const address = params.value as IAddressModel;
                const hasCoordinates =
                  address?.latitude != null && address?.longitude != null;
                const addressText = address?.formattedAddress || "";

                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      height: "100%",
                    }}
                  >
                    {hasCoordinates && (
                      <Tooltip title="Verified Address">
                        <PlaceIcon
                          color="primary"
                          sx={{
                            fontSize: "small",
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title={addressText} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {addressText}
                      </Typography>
                    </Tooltip>
                  </Box>
                );
              },
            },
            {
              field: "price",
              headerName: "Price",
              minWidth: isPipeline ? 90 : 100,
              flex: isPipeline ? 0.7 : 1,
              type: "number",
              sortable: true,
              sortingOrder: ["asc", "desc"],
              valueFormatter: (params: number) => {
                if (params === null || params === undefined) return "";
                return `$${params.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}`;
              },
            },
            {
              field: "clients",
              headerName: "Clients",
              minWidth: 200,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              flex: 2,
              valueFormatter: (params: ILookupItemSimpleModel[]) => {
                if (!params || params.length === 0) return "";
                return params.map((client) => client.name).join(", ");
              },
              // renderCell: (params) => {
              //   const clients = params.value as ILookupItemSimpleModel[];
              //   if (!clients || clients.length === 0) return "";
              //   return (
              //     <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              //       {clients.map((client) => client.name).join(", ")}
              //     </Box>
              //   );
              // },
              // valueFormatter: (params: ILookupItemSimpleModel[]) => {
              //   if (!params || params.length === 0) return "";
              //   return params.map((x) => x.name).join(", ");
              // },
            },
            {
              field: "sourceId",
              headerName: "Source",
              minWidth: 120,
              flex: 1,
              sortable: true,
              filterable: false,
              sortingOrder: ["asc", "desc"],
              sortComparator: (v1, v2, cellParams1, cellParams2) => {
                const source1 = userData?.sources?.find((x) => x.id === v1);
                const source2 = userData?.sources?.find((x) => x.id === v2);

                return (source1?.name ?? "").localeCompare(
                  source2?.name ?? "",
                );
              },
              // valueGetter: (params) => params.row.sourceId,
              valueFormatter: (params: number) => {
                const source = userData?.sources?.find(
                  (x) => x.id === params,
                );
                if (source) {
                  return source.name;
                }
              },
            },

            {
              field: "reasonId",
              headerName: "Reason",
              sortable: true,
              filterable: false,

              sortingOrder: ["asc", "desc"],
              sortComparator: (v1, v2, cellParams1, cellParams2) => {
                const reason1 = userData?.reasons?.find((x) => x.id === v1);
                const reason2 = userData?.reasons?.find((x) => x.id === v2);

                return (reason1?.name ?? "").localeCompare(
                  reason2?.name ?? "",
                );
              },
              minWidth: 120,
              flex: 1,
              valueFormatter: (params: number) =>
                getReasonDecription(params),
            },
            ...(isPipeline
              ? []
              : [
                  {
                    field: "county",
                    headerName: "County",
                    sortable: true,
                    filterable: false,
                    sortingOrder: ["asc", "desc"],
                    sortComparator: (v1, v2, cellParams1, cellParams2) => {
                      return (v1 || "").localeCompare(v2 || "");
                    },
                    minWidth: 100,
                    flex: 1,
                    valueGetter: (value, row) => {
                      return row.address?.county || "";
                    },
                  } as GridColDef,
                ]),
            {
              field: "actions",
              type: "actions",
              headerName: "Actions",
              minWidth: 80,
              flex: 1,
              getActions: (params) => [
                <GridActionsCellItem
                  icon={<EditIcon />}
                  label="Edit"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/transactions/edit/${params.id}`);
                  }}
                />,
                <GridActionsCellItem
                  icon={<MoreVertIcon />}
                  label="More"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, Number(params.id));
                  }}
                />,
              ],
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: isPipeline ? 100 : 50 },
            },
          }}
          pageSizeOptions={[50, 100]}
          // Pipeline sections stack like one table, so drop the footer while
          // a section fits on a single page (100 is the grid's max page size)
          hideFooter={isPipeline && rows.length <= 100}
          disableRowSelectionOnClick
          // sx={{
          //   "& .positive": {
          //     color: "success.main",
          //     fontWeight: "bold",
          //   },
          //   "& .negative": {
          //     color: "error.main",
          //     fontWeight: "bold",
          //   },
          // }}
        />
      </Box>
    );

  return (
    <PageLayout>
      <LoadingDialog open={isLoading || isDeleting} />
      <Dialog
        open={deleteConfirmation?.open === true}
        onClose={() => setDeleteConfirmation({ id: undefined, open: false })}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            You’re about to permanently delete the selected transaction. Do you
            want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteConfirmation({ id: undefined, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              setDeleteConfirmation({
                id: deleteConfirmation?.id,
                open: false,
                confirmed: true,
              })
            }
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mb: 6, ml: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/dashboard">
              Dashboard
            </Link>
            <Typography color="text.primary">Transactions</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Stack spacing={1}>
            <Typography
              variant="h4"
              color="primary"
              component="h1"
              fontWeight="bold"
            >
              {isPipeline ? "Pipeline" : `Transactions for ${year}`}
            </Typography>
            {/* {error && (
                <Typography color="error" variant="body1">
                  {error}
                </Typography>
              )} */}
          </Stack>
          {/* {error && (
              <Typography color="error" variant="body1">
                {error}
              </Typography>
            )} */}
          <FormControl
            variant="outlined"
            size="small"
            sx={{ width: 200, ml: 2 }}
          >
            <InputLabel>Year</InputLabel>
            <Select
              value={derivedYearId ?? ""}
              label="Year"
              onChange={(e) =>
                navigate(
                  `/transactions/${
                    e.target.value === pipelineFolder?.id
                      ? PIPELINE_ROUTE
                      : years?.find((y) => y.id === e.target.value)?.year
                  }`,
                )
              }
            >
              {pipelineFolder?.id && (
                <MenuItem key="pipeline" value={pipelineFolder.id}>
                  Pipeline
                </MenuItem>
              )}
              {years?.map((y) => (
                <MenuItem key={y.id} value={y.id}>
                  {y.year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Transaction Search Section */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
              onClick={() => setSearchExpanded(!searchExpanded)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SearchIcon color="primary" />
                <Typography variant="h6" color="primary">
                  Transaction Search
                </Typography>
                {hasActiveFilters() && (
                  <Chip
                    label={`${getActiveFilterCount()} filter${
                      getActiveFilterCount() > 1 ? "s" : ""
                    } active`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {hasActiveFilters() && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                  >
                    Clear All
                  </Button>
                )}
                <IconButton>
                  {searchExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={searchExpanded}>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Client Name */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Client Name"
                      value={searchFilters.clientName}
                      onChange={(e) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          clientName: e.target.value,
                        }))
                      }
                      size="small"
                    />
                  </Grid>

                  {/* Address */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={searchFilters.address}
                      onChange={(e) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      size="small"
                    />
                  </Grid>

                  {/* Transaction Type */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Transaction Type</InputLabel>
                      <Select
                        multiple
                        value={searchFilters.typeId}
                        label="Transaction Type"
                        onChange={(e) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            typeId:
                              typeof e.target.value === "string"
                                ? []
                                : (e.target.value as number[]),
                          }))
                        }
                        input={<OutlinedInput label="Transaction Type" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as number[]).map((value) => {
                              const type = userData?.transactionTypes?.find(
                                (t) => t.id === value,
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={type?.name || value}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {userData?.transactionTypes?.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            <Checkbox
                              checked={
                                type.id !== undefined &&
                                searchFilters.typeId.includes(type.id)
                              }
                            />
                            <ListItemText primary={type.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Source */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        multiple
                        value={searchFilters.sourceId}
                        label="Source"
                        onChange={(e) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            sourceId:
                              typeof e.target.value === "string"
                                ? []
                                : (e.target.value as number[]),
                          }))
                        }
                        input={<OutlinedInput label="Source" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as number[]).map((value) => {
                              const source = userData?.sources?.find(
                                (s) => s.id === value,
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={source?.name || value}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {userData?.sources?.map((source) => (
                          <MenuItem key={source.id} value={source.id}>
                            <Checkbox
                              checked={
                                source.id !== undefined &&
                                searchFilters.sourceId.includes(source.id)
                              }
                            />
                            <ListItemText primary={source.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Custom Area</InputLabel>
                      <Select
                        multiple
                        value={searchFilters.areaId}
                        label="Custom Area"
                        onChange={(e) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            areaId:
                              typeof e.target.value === "string"
                                ? []
                                : (e.target.value as number[]),
                          }))
                        }
                        input={<OutlinedInput label="Custom Area" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as number[]).map((value) => {
                              const area = userData?.areas?.find(
                                (a) => a.id === value,
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={area?.name || value}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {userData?.areas?.map((area) => (
                          <MenuItem key={area.id} value={area.id}>
                            <Checkbox
                              checked={
                                area.id !== undefined &&
                                searchFilters.areaId.includes(area.id)
                              }
                            />
                            <ListItemText primary={area.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Reason */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Reason</InputLabel>
                      <Select
                        multiple
                        value={searchFilters.reasonId}
                        label="Reason"
                        onChange={(e) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            reasonId:
                              typeof e.target.value === "string"
                                ? []
                                : (e.target.value as number[]),
                          }))
                        }
                        input={<OutlinedInput label="Reason" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as number[]).map((value) => {
                              const reason = userData?.reasons?.find(
                                (r) => r.id === value,
                              );
                              return (
                                <Chip
                                  key={value}
                                  label={reason?.name || value}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {userData?.reasons?.map((reason) => (
                          <MenuItem key={reason.id} value={reason.id}>
                            <Checkbox
                              checked={
                                reason.id !== undefined &&
                                searchFilters.reasonId.includes(reason.id)
                              }
                            />
                            <ListItemText primary={reason.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Price Min */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Min Price"
                      type="number"
                      value={searchFilters.priceMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (
                          value === "" ||
                          (Number(value) >= 0 &&
                            Number.isInteger(Number(value)))
                        ) {
                          setSearchFilters((prev) => ({
                            ...prev,
                            priceMin: value ? Number(value) : "",
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent decimal point, minus sign, and 'e' (scientific notation)
                        if (
                          e.key === "." ||
                          e.key === "-" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "+"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      size="small"
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                      InputProps={{
                        startAdornment: <Typography>$</Typography>,
                      }}
                    />
                  </Grid>

                  {/* Price Max */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Max Price"
                      type="number"
                      value={searchFilters.priceMax}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (
                          value === "" ||
                          (Number(value) >= 0 &&
                            Number.isInteger(Number(value)))
                        ) {
                          setSearchFilters((prev) => ({
                            ...prev,
                            priceMax: value ? Number(value) : "",
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent decimal point, minus sign, and 'e' (scientific notation)
                        if (
                          e.key === "." ||
                          e.key === "-" ||
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "+"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      size="small"
                      inputProps={{
                        min: 0,
                        step: 1,
                      }}
                      InputProps={{
                        startAdornment: <Typography>$</Typography>,
                      }}
                    />
                  </Grid>

                  {/* Date From */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Date From"
                      type="date"
                      value={
                        searchFilters.dateFrom
                          ? searchFilters.dateFrom.format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={(e) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          dateFrom: e.target.value
                            ? dayjs(e.target.value)
                            : null,
                        }))
                      }
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>

                  {/* Date To */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Date To"
                      type="date"
                      value={
                        searchFilters.dateTo
                          ? searchFilters.dateTo.format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={(e) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          dateTo: e.target.value ? dayjs(e.target.value) : null,
                        }))
                      }
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Search Results Summary */}
                {hasActiveFilters() && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Showing {filteredTransactions.length} of{" "}
                      {transactions.length} transactions
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Card>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="flex-start"
        >
          <Grid
            size={{ xs: 12, sm: 6, md: 3 }}
            sx={{ display: "flex", justifyContent: "flex-start" }}
          >
            <Button
              variant="contained"
              onClick={(event) => {
                // Pipeline entries are always entered manually (no HUD/ALTA import)
                if (!isPipeline && (currentUser?.isPro || currentUser?.isAdmin)) {
                  setAnchorEl(event.currentTarget);
                } else {
                  navigate(`/transactions/add/${year}`);
                }
              }}
              startIcon={<AddIcon />}
              sx={{
                height: "fit-content",
                px: 3,
                py: 1,
                borderRadius: 1,
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              {isPipeline ? "Pipeline Entry" : "Closed Transaction"}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate(`/transactions/add/${year}`);
                }}
              >
                Enter Manually
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  setOpenContractDialog(true);
                }}
              >
                Import HUD/ALTA/CD
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>

        <Grid
          size={{ xs: 12, sm: 6, md: 3 }}
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          {/* <Button
            variant="outlined"
            startIcon={<MapIcon />}
            size="small"
            onClick={() => navigate(`/transactions/map/${year}`)}
          >
            Map View
          </Button> */}
          {!isMobile && !isTablet && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="cards" aria-label="Cards view">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Grid>

        {/* Transaction Summary - Showing Total Volume, Total Units, Average Price */}
        {/* <Box sx={{ mt: 2, mb: 4 }}>
          <TransactionSummaryContainer yearId={derivedYearId || undefined} />
        </Box> */}
        {isPipeline && overdueCount > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {overdueCount === 1
              ? "1 entry has a Potential Closing Date in the past."
              : `${overdueCount} entries have a Potential Closing Date in the past.`}{" "}
            Please update the Potential Closing Date, change the Status to
            Closed, or delete.
          </Alert>
        )}
        {isPipeline && derivedYearId && (
          <Box sx={{ mt: 2 }}>
            <PipelineSummaryContainer yearId={derivedYearId} />
          </Box>
        )}

        {/* <Box> */}
        {isPipeline && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, ml: 1, display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <DragIndicatorIcon fontSize="small" />
            Drag an entry by its handle to another section to change its
            Status.
          </Typography>
        )}
        {isPipeline ? (
          <DndContext
            sensors={dragSensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            {PIPELINE_SECTIONS.map((status) => {
              const rows = displayTransactions.filter(
                (row) => row.status === status,
              );
              const volume = rows.reduce((sum, row) => sum + (row.price ?? 0), 0);
              return (
                <PipelineDropSection key={status} status={status}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: statusColor(theme, status),
                      }}
                    />
                    {transactionStatusLabel(status)}
                    <Typography component="span" color="text.secondary">
                      ({rows.length} | $
                      {volume.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                      )
                    </Typography>
                  </Typography>
                  {rows.length === 0 ? (
                    <Typography color="text.secondary" sx={{ ml: 1, mt: 1 }}>
                      None
                    </Typography>
                  ) : (
                    renderTransactions(rows)
                  )}
                </PipelineDropSection>
              );
            })}
            {/* Follows the pointer or finger; no drop animation since the row moves right away */}
            <DragOverlay dropAnimation={null}>
              {activeRow && (
                <Paper
                  elevation={6}
                  sx={{
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "max-content",
                    maxWidth: 320,
                    cursor: "grabbing",
                  }}
                >
                  <DragIndicatorIcon fontSize="small" color="action" />
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: statusColor(theme, activeRow.status),
                    }}
                  />
                  <Typography variant="body2" noWrap>
                    {activeRow.address?.formattedAddress || "No address"}
                  </Typography>
                  {activeRow.price != null && (
                    <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0 }}>
                      $
                      {activeRow.price.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </Typography>
                  )}
                </Paper>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          renderTransactions(displayTransactions)
        )}
        {/* </Box> */}
        {/* </Card> */}
        <ContractUploadDialog
          open={openContractDialog}
          onClose={() => setOpenContractDialog(false)}
          onPreview={async (files: File[]) => {
            try {
              setIsImportingContracts(true);
              setSnackbar({
                open: true,
                message: "Processing contract files...",
                severity: "info",
              });
              const result = await extractSettlmentData(files);
              setSettlementPreview({
                ...result,
                data: result.data?.map((item) => ({
                  ...item,
                  propertyAddress: item.propertyAddress
                    ? removeCounty(item.propertyAddress)
                    : undefined,
                })),
              });
              setOpenContractDialog(false);
              setOpenSettlementPreview(true);
            } catch (error) {
              setSnackbar({
                open: true,
                message: "Failed to process contract files. Please try again.",
                severity: "error",
              });
            } finally {
              setIsImportingContracts(false);
            }
          }}
          isUploading={isImportingContracts}
          aria-labelledby="contract-upload-dialog-title"
        />
        <PreviewSettlementDialog
          open={openSettlementPreview}
          onClose={() => setOpenSettlementPreview(false)}
          onImport={async (data, action) => {
            try {
              setSnackbar({
                open: true,
                message: "Importing settlement data...",
                severity: "info",
              });
              const affectedRows = await insertSettlementData(data, action);
              setOpenSettlementPreview(false);
              setSnackbar({
                open: true,
                message: `Successfully imported ${affectedRows} settlement records`,
                severity: "success",
              });
              // Refresh transactions list after import
              if (derivedYearId) {
                queryClient.invalidateQueries({
                  queryKey: ["transactions", derivedYearId],
                });
              }
            } catch (error) {
              setSnackbar({
                open: true,
                message: "Failed to import settlement data. Please try again.",
                severity: "error",
              });
            }
          }}
          previewData={settlementPreview}
          isUploading={isImportingContracts}
          aria-labelledby="settlement-preview-dialog-title"
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
            elevation={6}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Menu for card actions */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </PageLayout>
  );
};

export default TransactionListing;
